import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { formatCurrency } from '../utils/formatting';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../components/DateTimeDisplays';
import { AddressOneLine } from '../components/shared/AddressOneLine';
import { useServiceAgreementModel } from '../hooks/useServiceAgreementModel';
import { TransientAuthType } from '../typewriter/enums/TransientAuthType';
import { AdditionalRatesSummaryRow } from '../components/AdditionalRates';
import { ServiceType } from '../typewriter/enums';
import { acceptServiceAgreement } from '../typewriter/controllers/BookingSubdomainController';

export const ServiceAgreement = () => {
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [fullName, setFullName] = useState('');
    const [model, setModel] = useState<models.ReviewBookingSubdomainServiceAgreementQuoteModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    const { serviceAgreementModel, fetchModel } = useServiceAgreementModel(token || '', setLoading);

    useEffect(() => {

        if (!token) {
            setError('Missing required token. Please use the link provided in your email.');
            setLoading(false);
        }
        if (serviceAgreementModel) {
            setModel(serviceAgreementModel);
            setLoading(false);
        }
    }, [serviceAgreementModel, token]);

    if (loading) {
        return (
            <Container className="text-center my-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading service agreement...</p>
            </Container>
        );
    }

    if (error || !model) {
        return (
            <Container className="my-5">
                <div className="alert alert-danger">
                    {error || 'Unable to load service agreement'}
                </div>
            </Container>
        );
    }

    const { loadingJobs, unloadingJobs } = model.quote.quoteModel;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!termsAccepted || !fullName) {
            return;
        }

        setLoading(true);
        setError(null);

         const acceptModel: models.AcceptServiceAgreementModel = {
            token: token!,
            fullName: fullName,
            accepted: termsAccepted
        };

        try {
            const result = await acceptServiceAgreement(acceptModel);

            if (result.success) {
                setSuccess('Service agreement accepted successfully');
            } else if (result.alreadyAccepted) {
                setSuccess('Service agreement already accepted');
            } else {
                console.error('Agreement submission failed:', result);
                setError('Failed to submit agreement. Please try again or contact support.');
            }
        } catch (err: any) {
            if (err.response) {
                const errorMessage = err.response.data?.message || 'Server returned an error';
                setError(`Failed to submit agreement: ${errorMessage}`);
            } else if (err.request) {
                setError('Failed to connect to server. Please check your internet connection.');
            } else {
                setError(`Request failed: ${err.message || 'Please try again'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Container className="my-5">
                <div className="alert alert-success" style={{ background: 'white', border: '1px solid #d4edda' }}>
                    <h2 style={{
                        color: '#F37021',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        margin: '1rem 0'
                    }}>
                        {success}
                    </h2>
                </div>
            </Container>
        );
    }

    return (
        <Container className="my-4">
            <Row className="mb-2">
                <Col xs={12} className="mb-3 text-left">
                    <img
                        src="/img/sml-logo2x.png"
                        style={{
                            width: '150px',
                            height: '70px',
                            maxWidth: '100%'
                        }}
                        className="d-inline-block brand-logo"
                        alt="Simple Moving Labor"
                    />
                </Col>
            </Row>
            <div className="border-top my-3" style={{ width: '100vw', position: 'relative', marginLeft: 'calc(-50vw + 50%)' }}></div>
            <Row className="mb-4">
                <Col xs={12} className="text-center">
                    <div className="my-3 mx-auto" style={{
                        maxWidth: '80%',
                        backgroundColor: 'white',
                        borderRadius: '50px',
                        padding: '5px 12px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            height: '8px',
                            backgroundColor: '#F37021',
                            border: 'none',
                            width: '100%',
                            borderRadius: '50px'
                        }}></div>
                    </div>
                    <h2 style={{
                        color: '#004080',
                        fontWeight: 'bold'
                    }}>Thank you for choosing SML Relocation</h2>
                </Col>
            </Row>

            <Row>
                <Col md={unloadingJobs && unloadingJobs.length > 0 ? 6 : 12}
                    className={unloadingJobs && unloadingJobs.length > 0 ? '' : 'mx-auto'}
                    style={unloadingJobs && unloadingJobs.length > 0 ? {} : { maxWidth: '600px' }}>
                    <div className="border p-3 mb-3">
                        <h5 className="mb-3"> {(!unloadingJobs || unloadingJobs.length === 0) ? 'Move Details:' : 'Loading Details:'}</h5>
                        {loadingJobs.map(job => (
                            <LoadingUnloadingDetails
                                key={job.jobID}
                                job={job}
                                isLoading={true}
                                showAsMove={!unloadingJobs || unloadingJobs.length === 0}
                            />
                        ))}
                        <div className="border-top my-3 w-100"></div>
                        {loadingJobs.map(job => (
                            <ServiceDetails
                                key={job.jobID}
                                job={job}
                                lineItems={job.lineItems}
                            />
                        ))}
                        <div className="border-top my-3 w-100"></div>
                        {loadingJobs.length > 0 && loadingJobs[0].totals && (
                            <>
                                <Row className="mt-4">
                                    <Col>Service Total For {loadingJobs[0].hoursAndHelpers?.laborHours} Hrs. Or Less:</Col>
                                    <Col xs="auto" className="text-end text-primary fw-bold">
                                        {formatCurrency(model.quote.grandOrderTotal, { truncateZeroCents: true })}*
                                    </Col>
                                </Row>
                                <AdditionalRatesSummaryRowLocal
                                    typeOfHelp="Loading"
                                    serviceType={loadingJobs[0].serviceType}
                                    totals={loadingJobs[0].totals}
                                />
                                <Row className="mt-1 fw-bold">
                                    <Col xs={12} className="text-center">
                                        <small>**Includes 15% Partner Discount**</small>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div>
                </Col>

                {unloadingJobs && unloadingJobs.length > 0 && (
                    <Col md={6}>
                        <div className="border p-3 mb-3">
                            <h5 className="mb-3">Unloading Details:</h5>
                            {unloadingJobs.map(job => (
                                <LoadingUnloadingDetails
                                    key={job.jobID}
                                    job={job}
                                    isLoading={false}
                                />
                            ))}
                            <div className="border-top my-3 w-100"></div>
                            {unloadingJobs.map(job => (
                                <ServiceDetails
                                    key={job.jobID}
                                    job={job}
                                    lineItems={job.lineItems}
                                />
                            ))}
                            <div className="border-top my-3 w-100"></div>
                            {unloadingJobs.length > 0 && unloadingJobs[0].totals && (
                                <>
                                    <Row className="mt-4">
                                        <Col>Service Total For {unloadingJobs[0].hoursAndHelpers?.laborHours} Hrs. Or Less:</Col>
                                        <Col xs="auto" className="text-end text-primary fw-bold">
                                            {formatCurrency(model.quote.grandOrderTotal, { truncateZeroCents: true })}*
                                        </Col>
                                    </Row>
                                    <AdditionalRatesSummaryRowLocal
                                        typeOfHelp="Unloading"
                                        serviceType={unloadingJobs[0].serviceType}
                                        totals={unloadingJobs[0].totals}
                                    />
                                    <Row className="mt-1 fw-bold ">
                                        <Col xs={12} className="text-center">
                                            <small>**Includes 15% Partner Discount**</small>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </div>
                    </Col>
                )}
            </Row>

            <Form onSubmit={handleSubmit}>
                    <h5 style={{
                        fontWeight: 'bold'
                    }}>Please read and accept Terms of Service</h5>
                <div className="terms-of-service border p-3 my-3" style={{ height: '200px', overflowY: 'auto', borderRadius: '10px' }} dangerouslySetInnerHTML={{ __html: model.customerServiceAgreementHtml }}>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="termsCheckbox"
                            label={<strong>I've read and accept the Terms of Service</strong>}  
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Type your Full Name"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                        />
                    </Form.Group>

                    <div className="text-center">
                        <Button
                            variant="primary"
                            type="submit"
                            className="px-4"
                            style={{
                                backgroundColor: '#273a7e',
                                color: 'white',
                                borderColor: 'black',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                borderRadius: '50px'
                            }}
                            disabled={!termsAccepted || !fullName}
                        >
                            Agree & Submit
                        </Button>
                    </div>
            </Form>
        </Container>
    );
};

const LoadingUnloadingDetails = ({ job, isLoading, showAsMove = false }: { job: models.BookingSubdomainJobInfo, isLoading: boolean, showAsMove?: boolean }) => {
    const label = isLoading ? (showAsMove ? 'Move' : 'Loading') : 'Unloading';
    const { jobAddresses, jobDate, desiredArrivalWindow } = job;

    if (!jobAddresses || jobAddresses.length === 0) return null;

    const arrivalWindow = desiredArrivalWindow?.replace('Arrival Between', '');
    const formattedDate = formatDate(jobDate!);

    return (
        <>
            <div className="mb-3">
                <div className="fw-bold">{label} Location</div>
                {jobAddresses.map((address, index) => (
                    <div key={index} className="mb-2">
                        {index > 0 && <div className="text-muted">2nd stop:</div>}
                        <AddressOneLine {...address} />
                    </div>
                ))}
            </div>

            <div className="mb-3">
                <div className="fw-bold">{label} Date</div>
                <div>{formattedDate}</div>
                <div>Arrival Between {arrivalWindow}</div>
            </div>
        </>
    );
};

const ServiceDetails = ({ job, lineItems }: { job: models.BookingSubdomainJobInfo, lineItems: readonly models.BookingSubdomainLineItem[] }) => {
    if (!job.hoursAndHelpers || !job.totals) return null;

    const { hoursAndHelpers, totals } = job;
    const serviceTotal = totals.orderTotal || 0;

    return (
        <>
            <div className="mt-4">
                <div className="fw-bold">Service Details:</div>
                <Row className="mt-2">
                    <Col>(
                        <span className="text-primary">{hoursAndHelpers.numHelpers}</span>
                        ) Crew Members For (
                        <span className="text-primary">{hoursAndHelpers.laborHours}</span>
                        ) Hours Min.
                    </Col>
                    <Col xs="auto" className="text-end fw-bold">
                        {formatCurrency(serviceTotal, { truncateZeroCents: true })}
                    </Col>
                </Row>

                {lineItems && lineItems
                    .filter(lineItem => lineItem.total !== 0 && lineItem.total != null)
                    .map((lineItem, index) => (
                        <Row key={index}>
                            <Col>{lineItem.name}:</Col>
                            <Col xs="auto" className="text-end">
                                {formatCurrency(lineItem.total || 0, { truncateZeroCents: true })}
                            </Col>
                        </Row>
                    ))}
            </div>
        </>
    );
};

const SummaryRow = ({ label, description, amount }: { label?: string, description?: React.ReactElement, amount?: React.ReactElement }) => {
    return (
        <Row className="mt-2">
            <Col><strong>{label}</strong></Col>
            {description && (
                <Col>
                    {description}
                </Col>
            )}
            <Col xs="auto" className="text-end fw-bold">
                {amount}
            </Col>
        </Row>
    );
}

const AdditionalRatesSummaryRowLocal = ({ serviceType, totals, typeOfHelp }: {
    serviceType?: ServiceType,
    totals: models.BookingSubdomainJobTotals,
    typeOfHelp: string
}) => {
    if (!totals.additionalRates) return null;
    if (serviceType === ServiceType.ManagedLaborPlus) {
        return (
            <SummaryRow
                label={`Additional ${typeOfHelp} Rates`}
                description={
                    <div className="d-flex flex-column">
                        {totals.additionalRates.map(r =>
                            <span key={r.id}>{`${r.name} - ${formatCurrency(r.customerPrice, { truncateZeroCents: true })}/${r.unitOfMeasurement}`}</span>)}
                    </div>
                }
            />
        );
    } else {
        return (
            <SummaryRow
                label="Additional Hourly Rate:"
                amount={<>{formatCurrency(totals.additionalRates[0]?.customerPrice, { truncateZeroCents: true })}/hr*</>}
            />
        );
    }
}

